<template>
	<Col class="suggestions-content-container px-5 pb-5 pt-4 flex-justify-space-between">
		<Col class="height-100 overflow-y-auto">
			<Row class="title-text mb-1">Reassign connections</Row>
			<Row class="title-description mb-2">
				Some apps like email, calendar and messaging might require your users to connect their own account, as you don’t
			</Row>
			<Row class="title-description mb-2">
				have access to their data, or can’t run actions on their behalf.&nbsp;&nbsp;
				<SuggestCredentialsTypeDialogContentLearnMoreMenu />
			</Row>
			<h3 class="reg review-text mb-3">Review recommended changes to connections:</h3>
			<Row class="width-100 flex-centerize my-5" v-if="areNodesLoading">
				<AppLoader class="flex-centerize" />
			</Row>
			<NodeCredentialsSuggestion
				v-show="!areNodesLoading"
				v-for="nodeId in allNodeIdsToSuggestIncludingWidgets"
				class="mb-2"
				:key="nodeId"
				:nodeId="nodeId"
				:fromPublishFlow="isFromPublish"
				:widgetData="getWidgetData(nodeId)"
				:isLoading="nodesLoadingMap[nodeId]"
				@update-accept-suggestion="updateAcceptSuggestion"
				@update-node-loading-state="updateNodeLoadingState"
				:acceptSuggestion="getNodeAcceptSuggestionState(nodeId)"
			/>
			<template v-if="!areNodesLoading">
				<CredentialsSuggestionsInfoMessage
					class="mt-3"
					:key="message.text"
					:text="message.text"
					:isWarning="message.warning"
					v-for="message in infoMessagesToShow"
				/>
			</template>
		</Col>
		<Row class="width-100 flex-centerize mt-5">
			<AnemicButton @click="onSave" :loading="isSaving" :disabled="isSaving" tile class="save-button">
				{{ saveButtonText }}
			</AnemicButton>
		</Row>
	</Col>
</template>

<script>
import { cloneDeep } from 'lodash';
import { getCredentialsSuggestions } from '@/store/modules/credentials/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { acceptCredentialsSuggestions } from '@/store/modules/credentials/actions';
import NodeCredentialsSuggestion from '@/components/dialogs/SuggestCredentialsTypeDialog/NodeCredentialsSuggestion.vue';
import CredentialsSuggestionsInfoMessage from '@/components/dialogs/SuggestCredentialsTypeDialog/CredentialsSuggestionsInfoMessage.vue';
import SuggestCredentialsTypeDialogContentLearnMoreMenu from '@/components/dialogs/SuggestCredentialsTypeDialog/SuggestCredentialsTypeDialogContentLearnMoreMenu.vue';

export default {
	name: 'SuggestCredentialsTypeDialogContent',
	components: {
		NodeCredentialsSuggestion,
		CredentialsSuggestionsInfoMessage,
		SuggestCredentialsTypeDialogContentLearnMoreMenu,
	},
	props: {
		next: { type: Function },
		workflow: { type: Object },
		isFromPublish: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ getCredentialsSuggestions }),
		saveButtonText() {
			if (this.didIgnoreAllSuggestions) {
				return 'Save and skip recommendation';
			}
			if (this.didIgnoreSomeSuggestions) {
				return 'Save changes';
			}
			return 'Save recommended changes';
		},
		didIgnoreAllSuggestions() {
			return this.amountOfSuggestions === this.amountOfIgnoredSuggestions;
		},
		didIgnoreSomeSuggestions() {
			return this.amountOfIgnoredSuggestions > 0;
		},
		amountOfSuggestions() {
			return this.allNodeIdsToSuggestIncludingWidgets?.length || 0;
		},
		amountOfIgnoredSuggestions() {
			return this.amountOfSuggestions - this.nodeIdsToApplySuggestion?.length || 0;
		},
		regularNodesToSuggest() {
			return this.getCredentialsSuggestions(this.workflow.id)?.move_to_user_configuration || [];
		},
		widgetNodesToSuggest() {
			return this.getCredentialsSuggestions(this.workflow.id)?.search_fields_to_move_to_user_configuration || {};
		},
		widgetNodesIdsToSuggest() {
			return Object.keys(this.widgetNodesToSuggest).map((x) => Number(x));
		},
		areNodesLoading() {
			return Object.values(this.nodesLoadingMap).some((isLoading) => isLoading);
		},
		allNodeIdsToSuggestIncludingWidgets() {
			return [...this.regularNodesToSuggest, ...this.widgetNodesIdsToSuggest];
		},
		infoMessages() {
			return [
				{
					show: !this.didIgnoreAllSuggestions,
					text: 'A personal setup will be created, users will be prompted to connect their accounts.',
				},
				{
					show: !this.didIgnoreAllSuggestions,
					text: 'You’ll still use your connections for these steps - they will be used in your personal setup',
				},
				{
					warning: true,
					show: this.didIgnoreSomeSuggestions,
					text: 'Recommendations you ignore will not appear again. You can always make changes by editing the step in the feature builder.',
				},
			];
		},
		infoMessagesToShow() {
			return this.infoMessages.filter((message) => message.show);
		},
	},
	methods: {
		...mapActionsShorthand({ acceptCredentialsSuggestions }),
		getNodeAcceptSuggestionState(nodeId) {
			return this.nodeIdsToApplySuggestion.includes(nodeId);
		},
		updateNodeLoadingState({ nodeId, isLoading }) {
			this.nodesLoadingMap = { ...this.nodesLoadingMap, [nodeId]: isLoading };
		},
		updateAcceptSuggestion({ nodeId, acceptSuggestion }) {
			if (acceptSuggestion && !this.nodeIdsToApplySuggestion.includes(nodeId)) {
				this.nodeIdsToApplySuggestion = [...this.nodeIdsToApplySuggestion, nodeId];
			} else if (this.nodeIdsToApplySuggestion.includes(nodeId)) {
				this.nodeIdsToApplySuggestion = this.nodeIdsToApplySuggestion.filter((id) => id !== nodeId);
			}
		},
		async onSave() {
			this.isSaving = true;
			try {
				await this.acceptCredentialsSuggestions({
					workflowId: this.workflow.id,
					fromPublishFlow: this.isFromPublish,
					nodesToChange: this.nodeIdsToApplySuggestion,
					markNotToSuggest: this.allNodeIdsToSuggestIncludingWidgets.filter((id) => !this.nodeIdsToApplySuggestion.includes(id)),
				});
				this.$emit('close');
				this.$emit('accepted-suggestions');
				if (this.next) {
					await this.next();
				}
			} catch (e) {
				console.error('acceptCredentialsSuggestions error - ', e);
				this.$toast.error('Failed to save credentials suggestions, please try again later');
			} finally {
				this.isSaving = false;
			}
		},
		getWidgetData(nodeId) {
			return this.widgetNodesToSuggest[nodeId] || [];
		},
	},
	async created() {
		this.nodeIdsToApplySuggestion = cloneDeep(this.allNodeIdsToSuggestIncludingWidgets);
		this.nodeIdsToApplySuggestion.map((nodeId) => (this.nodesLoadingMap[nodeId] = true));
	},
	data() {
		return {
			isSaving: false,
			nodesLoadingMap: {},
			nodeIdsToApplySuggestion: null,
		};
	},
};
</script>

<style scoped lang="scss">
.suggestions-content-container {
	width: 620px;
	height: 620px;
}
.title-text {
	font-size: 18px;
	font-weight: 600;
	color: var(--primary-font-color);
}
.title-description {
	font-size: 11px;
	line-height: 12px;
	letter-spacing: 0px;
	color: var(--primary-font-color);
}
.review-text {
	font-size: 14px;
	font-weight: 600;
	color: #424242;
	font-weight: 600;
	letter-spacing: 0px;
}
.save-button {
	width: 260px !important;
	height: 45px !important;
	font-size: 14px !important;
	letter-spacing: 0px !important;
	font-weight: normal !important;
	color: var(--white) !important;
	border-radius: 10px !important;
	text-transform: none !important;
	background: var(--bold-border-color) !important;
	border: 1px solid var(--bold-border-color) !important;
	&:disabled {
		border: unset !important;
	}
}
</style>
